import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DateService } from '../../../../common/services/date.service';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { SubHeader } from '../../../../core/models/sub-header.model';
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { ReportSearchCriteria } from '../../../models/admin-reports.model';
import { AdhocReportsService } from '../../../services/adhoc-reports-service';
import { tap } from 'rxjs/operators';
import { Dialog, DialogButton } from '../../../../core/models/dialog.model';
import { DynamicDialogService } from '../../../../core/services/dynamic-dialog.service';

@Component({
    selector: 'app-county-engineers-report',
    templateUrl: './county-engineers-report.component.html',
    styleUrls: ['./county-engineers-report.component.scss'],
    standalone: false
})
export class CountyEngineersReportComponent implements OnInit {
  reportData: any[];
  gridOptions: GridOptions;
	gridApi: GridApi;
  currentDate: Date = new Date();

    get invalidDateCombination(): boolean {
        let invalid: boolean = false;
        if (this.toDate != null && this.fromDate != null) {
            invalid = this.toDate < this.fromDate;
        }
        return invalid;
    }

    toDate: Date | null;
    fromDate: Date | null;

    subHeader: SubHeader= new SubHeader({
        title: "County Engineers Report",
        buttons: []
    });

    subscription: Subscription = new Subscription();


  constructor(private datePipe: DatePipe,
    private readonly dateService: DateService,
    private readonly formatService: GridFormatterService,
    private readonly adhocReportService: AdhocReportsService,
    public subHeaderService: SubHeaderService,
	  public dialogService: DynamicDialogService) {
  }

  ngOnInit() {
      this.subHeaderService.updateSubHeader(this.subHeader);
      this.fromDate = this.dateService.generateDateWithOffset(0, -1, 0);
      this.toDate = this.currentDate;
      let coldefs = this.createColumnDefinitions();
      this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(coldefs, false, true, true, true);
      this.gridOptions.headerHeight = 50;
      this.gridOptions.suppressColumnVirtualisation = true;
	  this.gridOptions.onGridReady = (params) => {
		  this.gridApi = params.api;
	  }
      this.generateReport();
  }

  generateReport() {
      if (this.gridApi) {
		  this.gridApi.setGridOption("loading", true);
      }

      const criteria = new ReportSearchCriteria();
      criteria.fromDate = this.fromDate;
      criteria.toDate = this.toDate;

      this.subscription = this.adhocReportService.getCountyEngineersReportData(criteria).pipe(
          tap((data) => {
              this.reportData = data;
          })).subscribe({
            next: (success) => {
			    this.gridApi.setGridOption('rowData', this.reportData);
			    this.gridApi.setGridOption("loading", false);
			    this.gridApi.sizeColumnsToFit();
            }, 
            error: (fail) => {
                console.error(fail);
			    this.gridApi.setGridOption("loading", false);
                this.showError();
            }
        });
  }

  public createColumnDefinitions() {
      return [
          {
              headerClass: "wrapped",
              headerName: "Permit Application Number",
              headerTooltip: "Permit Application Number",
              field: "applicationNumber",
              tooltipField: "applicationNumber",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 200
          },
          {
              headerClass: "wrapped",
              headerName: "Permit Application Received Date",
              headerTooltip: "Permit Application Received Date",
              field: "applicationSubmitDate",
              tooltipValueGetter: this.formatService.formatDate("applicationSubmitDate"),
              valueGetter: this.formatService.formatDate("applicationSubmitDate"),
              comparator: dateComparator,
              filter: "agDateColumnFilter",
              filterParams: this.formatService.defaultDateFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "Well Name",
              headerTooltip: "Well Name",
              tooltipField: "wellName",
              field: "wellName",
              filter: "agNumberColumnFilter",
              filterParams: this.formatService.defaultNumberFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "Well Number",
              headerTooltip: "Well Number",
              tooltipField: "wellNumber",
              field: "wellNumber",
              filter: "agNumberColumnFilter",
              filterParams: this.formatService.defaultNumberFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "County",
              headerTooltip: "County",
              field: "county",
              tooltipField: "county",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "Civil Township",
              headerTooltip: "Civil Townshop",
              field: "township",
              tooltipField: "township",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "Section",
              headerTooltip: "Section",
              field: "section",
              tooltipField: "section",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "Lot",
              headerTooltip: "Lot",
              field: "lot",
              tooltipField: "lot",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 100
          }, 
          {
              headerClass: "wrapped",
              headerName: "Quadrangle",
              headerTooltip: "Quadrangle",
              field: "quadrangle",
              tooltipField: "quadrangle",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 125
          }, 
          {
              headerClass: "wrapped",
              headerName: "RICS Owner Number",
              headerTooltip: "RICS Owner Number",
              field: "organizationNumber",
              tooltipField: "organizationNumber",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "RBDMS Owner Number",
              headerTooltip: "RBDMS Owner Number",
              field: "organizationRBDMSNumber",
              tooltipField: "organizationRBDMSNumber",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "Owner Name",
              headerTooltip: "Owner Name",
              field: "organizationName",
              tooltipField: "organizationName",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 200
          }, 
          {
              headerClass: "wrapped",
              headerName: "Owner Street Address",
              headerTooltip: "Owner Street Address",
              field: "organizationStreet",
              tooltipField: "organizationStreet",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 200
          }, 
          {
              headerClass: "wrapped",
              headerName: "Owner Suite",
              headerTooltip: "Owner Suite",
              field: "organizationSuite",
              tooltipField: "organizationSuite",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "Owner PO Box",
              headerTooltip: "Owner PO Box",
              field: "organizationPoBox",
              tooltipField: "organizationPoBox",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "Owner City",
              headerTooltip: "Owner City",
              field: "organizationCity",
              tooltipField: "organizationCity",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "Owner Zip",
              headerTooltip: "Owner Zip",
              field: "organizationZipCode",
              tooltipField: "organizationZipCode",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "Owner Phone Number",
              headerTooltip: "Owner Phone Number",
              field: "organizationPhoneNumber",
              tooltipField: "organizationPhoneNumber",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "Well Type",
              headerTooltip: "Well Type",
              field: "wellType",
              tooltipField: "wellType",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "Existing",
              headerTooltip: "Existing",
              tooltipValueGetter: this.formatStringToYesNo(),
              valueFormatter: this.formatStringToYesNo(),
              field: "isExistingWell",
              tooltipField: "isExistingWell",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "Application Type",
              headerTooltip: "Application Type",
              field: "applicationType",
              tooltipField: "applicationType",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 200
          },
          {
              headerClass: "wrapped",
              headerName: "Permitted Formations",
              headerTooltip: "Permitted Formations",
              field: "permittedFormations",
              tooltipField: "permittedFormations",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 150
          },
          {
              headerClass: "wrapped",
              headerName: "Acreage",
              headerTooltip: "Acreage",
              field: "acreage",
              tooltipField: "acreage",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultNumberFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "Latitude",
              headerTooltip: "Latitude",
              field: "latitude",
              tooltipField: "latitude",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultNumberFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "Longitude",
              headerTooltip: "Longitude",
              field: "longitude",
              tooltipField: "longitude",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultNumberFilterParams,
              minWidth: 125
          },
          {
              headerClass: "wrapped",
              headerName: "State, County, Township, Municipal Roads for Ingress to wellsite",
              headerTooltip: "State, County, Township, Municipal Roads for Ingress to wellsite",
              field: "ingress",
              tooltipField: "ingress",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 200
          },
          {
              headerClass: "wrapped",
              headerName: "State, County, Township, Municipal Roads for Egress to wellsite",
              headerTooltip: "State, County, Township, Municipal Roads for Egress to wellsite",
              field: "egress",
              tooltipField: "egress",
              filter: "agTextColumnFilter",
              filterParams: this.formatService.defaultTextFilterParams,
              minWidth: 200
          }
      ] as ColDef[];
  }

  downloadReport() {
      const criteria = new ReportSearchCriteria();
      criteria.fromDate = this.fromDate;
      criteria.toDate = this.toDate;
      let fileName = "CountyEngineersReport";
      if (this.fromDate && this.toDate) {
        const fromDate = this.datePipe.transform(this.fromDate, "yyyy-MM-dd");
        const toDate = this.datePipe.transform(this.toDate, "yyyy-MM-dd");
        fileName = `CountyEngineersReport_${fromDate}_${toDate}`;
      }
      
      const sheetName = "County Engineers Report";
      this.formatService.exportDataToExcel(fileName, sheetName, this.gridApi);
  }

	fromDateChanged(model: Date): void {
      this.fromDate = model;
  }

	toDateChanged(model: Date): void {
      this.toDate = model;
  }

  resizeColumns(): void {
      if (this.gridApi) {
          this.gridApi.sizeColumnsToFit();
      }
  }

  public formatStringToYesNo(): any {
      return (obj: any) => {
          if (obj.data) {
              if (obj.data.isExistingWell === "true") {
                return "Yes";
              }
              return "No";
          }
      }
  }

  showError(): void {
      this.errorDialog.toggleDialog(true);
      this.dialogService.updateDialog(this.errorDialog);
  }

  errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => { return; })]);

}
